import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { findVariant, type CreativeVariant } from '../data/creativeVariants';
import { translate, type StringKey } from '../data/i18n';
import type { PricingConfig } from '../data/pricingConfig';
import { evaluateQuote } from '../utils/quoteEngine';
import { loadPricingConfig } from '../utils/pricingStore';
import { newId, recordEvent, saveLead } from '../utils/sandboxStore';
import { submitEstimateRequest } from '../utils/bookingApi';
import { propertyFor } from '../data/propertyImages';
import { normalizeInventory } from '../data/inventory';
import { buildMoveSummary } from '../utils/moveSummary';
import { fireLead, fireViewContent, moveContentId } from '../utils/metaPixel';
import type { AccessInfo, Bedrooms, FunnelData, Lang, QuoteResult, SandboxLead } from '../types/funnel';
export const STEPS: StringKey[] = ['stepSize', 'stepAddresses', 'stepAccess', 'stepInventory', 'stepPacking', 'stepDate', 'stepContact', 'stepEstimate', 'stepReserve', 'stepStatus'];
export const STEP_ESTIMATE = 7;
export const STEP_RESERVE = 8;
export const STEP_STATUS = 9;
const STORAGE_KEY = 'im_funnel_progress_v2';
export const CONSENT_VERSION = 'consent-draft-2026-10-06';
const ATTR_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'creative_key', 'ad_id', 'adset_id', 'campaign_id', 'meta_lead_id', 'fbclid'];
export const defaultAccess: AccessInfo = {
  floor: 'ground',
  flights: 0,
  method: 'stairs',
  elevatorReservation: 'unknown',
  sharedElevator: 'unknown',
  truckNearEntrance: 'unknown',
  carryBand: 'unsure',
  carryFeet: '',
  parking: 'unsure',
  innerCarryBand: 'unsure',
  restrictions: '',
  loadingHours: '',
  gateAccess: 'unknown',
  coiRequired: 'unknown',
  notes: ''
};
function initialData(variant: CreativeVariant, lang: Lang): FunnelData {
  return {
    homeType: variant.preset.homeType ?? null,
    bedrooms: variant.preset.bedrooms ?? null,
    originZip: '',
    originStreet: '',
    originUnit: '',
    destZip: '',
    destStreet: '',
    destUnit: '',
    originBuilding: '',
    destBuilding: '',
    originBuildingNo: '',
    destBuildingNo: '',
    originCity: '',
    originState: '',
    originPlaceId: null,
    originAddrStatus: 'typed',
    destCity: '',
    destState: '',
    destPlaceId: null,
    destAddrStatus: 'typed',
    stops: [],
    origin: {
      ...defaultAccess
    },
    destination: {
      ...defaultAccess
    },
    officeSize: null,
    inventory: {},
    itemNotes: {},
    services: {
      reassembly: false,
      placement: false,
      garageOnly: false,
      garageOnlyNote: '',
      unpacking: false
    },
    truckPreference: null,
    customItems: [],
    inventoryUnsure: false,
    needsDisassembly: false,
    specialItems: [],
    inventoryNotes: '',
    packing: null,
    packingProducts: {},
    moveDate: '',
    dateUnsure: false,
    arrival: null,
    arrivalOther: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    commLang: lang,
    contactChannel: 'call',
    consentQuote: false,
    consentSms: false,
    consentAiCall: false,
    crewPreference: null,
    acceptedQuoteHash: null,
    thirdPartyPayer: false,
    payerName: '',
    payerEmail: '',
    payerPhone: '',
    paymentMethod: 'card'
  };
}
interface Stored {
  data: FunnelData;
  step: number;
  started: boolean;
  lang: Lang;
  /** True only when the visitor used the EN/ES switch; otherwise the browser language is used on every visit. */
  langManual?: boolean;
  creativeKey: string;
  submitted: SandboxLead | null;
  sessionId: string;
  attribution: Record<string, string>;
}
interface FunnelCtx {
  data: FunnelData;
  update: (patch: Partial<FunnelData>) => void;
  updateAccess: (end: 'origin' | 'destination', patch: Partial<AccessInfo>) => void;
  step: number;
  goTo: (n: number) => void;
  started: boolean;
  start: (bedrooms?: Bedrooms) => void;
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
  variant: CreativeVariant;
  quote: QuoteResult;
  config: PricingConfig;
  estimateAccepted: boolean;
  acceptEstimate: () => void;
  submitted: SandboxLead | null;
  submit: () => Promise<void>;
  reset: () => void;
  track: (name: string, props?: Record<string, string | number>) => void;
}
const Ctx = createContext<FunnelCtx | null>(null);

/** First visit only: first supported browser language wins (es → Spanish, en → English), fallback English. Manual choice is stored and wins afterwards. */
function detectLang(): Lang {
  if (typeof navigator === 'undefined') return 'en';
  const list = [...(navigator.languages ?? []), navigator.language].filter(Boolean);
  for (const l of list) {
    const p = l.toLowerCase().slice(0, 2);
    if (p === 'es') return 'es';
    if (p === 'en') return 'en';
  }
  return 'en';
}
function readAttribution(): Record<string, string> {
  const p = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {
    landing_path: window.location.pathname
  };
  for (const k of ATTR_KEYS) {
    const v = p.get(k);
    if (v) out[k] = v.slice(0, 120);
  }
  return out;
}
export function FunnelProvider({
  creativeKey,
  children
}: {
  creativeKey: string;
  children: React.ReactNode;
}) {
  const variant = findVariant(creativeKey);
  const [state, setState] = useState<Stored>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Stored;
        if (parsed.creativeKey === variant.key && parsed.data) {
          // Merge onto current defaults so drafts saved by an older version keep working.
          // A visitor's own EN/ES choice is remembered; otherwise the browser language is used on every visit.
          // Language is always automatic: taken from the browser on every visit.
          const l = detectLang();
          const merged = {
            ...initialData(variant, l),
            ...parsed.data,
            commLang: l
          };
          const upgradeAccess = (a: AccessInfo) => ({
            ...defaultAccess,
            ...a
          });
          return {
            ...parsed,
            lang: l,
            data: {
              ...merged,
              origin: upgradeAccess(merged.origin),
              destination: upgradeAccess(merged.destination),
              stops: (merged.stops ?? []).map(s => ({
                buildingNo: '',
                ...s,
                access: upgradeAccess(s.access)
              })),
              inventory: normalizeInventory(merged.inventory)
            }
          };
        }
      }
    } catch {
      /* start fresh */
    }
    const l = detectLang();
    return {
      data: initialData(variant, l),
      step: 0,
      started: false,
      lang: l,
      creativeKey: variant.key,
      submitted: null,
      sessionId: newId('S'),
      attribution: readAttribution()
    };
  });
  const [config] = useState<PricingConfig>(() => loadPricingConfig());
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);
  useEffect(() => {
    document.documentElement.lang = state.lang;
  }, [state.lang]);
  useEffect(() => {
    recordEvent('PageView', `${state.sessionId}:${state.started ? state.step : 'home'}`, {
      creative_key: variant.key,
      screen: state.started ? STEPS[state.step] : 'home'
    });
  }, [state.sessionId, state.started, state.step, variant.key]);

  // Meta ViewContent: once per move size the visitor actually selects (stable content IDs, no personal data).
  useEffect(() => {
    if (!state.started || !state.data.bedrooms) return;
    fireViewContent(moveContentId(state.data.bedrooms), {
      content_category: state.data.homeType ?? 'unspecified',
      creative_key: variant.key
    });
  }, [state.started, state.data.bedrooms, state.data.homeType, variant.key]);
  const track = useCallback((name: string, props: Record<string, string | number> = {}) => recordEvent(name, `${state.sessionId}:${JSON.stringify(props)}`, {
    creative_key: variant.key,
    ...props
  }), [state.sessionId, variant.key]);
  const update = useCallback((patch: Partial<FunnelData>) => setState(s => ({
    ...s,
    data: {
      ...s.data,
      ...patch
    }
  })), []);
  const updateAccess = useCallback((end: 'origin' | 'destination', patch: Partial<AccessInfo>) => setState(s => ({
    ...s,
    data: {
      ...s.data,
      [end]: {
        ...s.data[end],
        ...patch
      }
    }
  })), []);
  const quote = useMemo(() => evaluateQuote(state.data, config), [state.data, config]);
  const goTo = useCallback((n: number) => {
    setState(s => ({
      ...s,
      step: Math.max(0, Math.min(STEPS.length - 1, n))
    }));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  const start = useCallback((bedrooms?: Bedrooms) => {
    setState(s => ({
      ...s,
      started: true,
      step: s.submitted ? STEP_STATUS : 0,
      data: bedrooms ? {
        ...s.data,
        bedrooms
      } : s.data
    }));
    recordEvent('IntakeStarted', state.sessionId, {
      creative_key: variant.key
    });
    if (bedrooms) recordEvent('MoveSizeSelected', `${state.sessionId}:${bedrooms}`, {
      bedrooms,
      creative_key: variant.key
    });
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [state.sessionId, variant.key]);
  const acceptEstimate = useCallback(() => {
    setState(s => ({
      ...s,
      data: {
        ...s.data,
        acceptedQuoteHash: quote.hash
      }
    }));
    recordEvent('EstimateAccepted', `${state.sessionId}:${quote.hash}`, {
      quote: quote.hash,
      creative_key: variant.key
    });
  }, [quote.hash, state.sessionId, variant.key]);
  const submit = useCallback(async () => {
    const d = state.data;
    const res = await submitEstimateRequest(d, quote, state.attribution);
    const moveSummary = buildMoveSummary(d);
    const now = new Date().toISOString();
    const lead: SandboxLead = {
      id: res.bookingId,
      createdAt: now,
      mode: res.mode,
      creativeKey: variant.key,
      requestType: quote.zone === 'outside' ? 'zone_review' : 'estimate_request',
      estimateStatus: d.acceptedQuoteHash === quote.hash ? 'accepted' : 'draft',
      signatureStatus: 'not_started',
      depositStatus: 'not_started',
      dispatchStatus: 'pending_review',
      quoteHash: quote.hash,
      configVersion: quote.configVersion,
      summary: {
        name: `${d.firstName} ${d.lastName}`.trim(),
        bedrooms: d.bedrooms ?? '—',
        homeType: d.homeType ?? '—',
        route: `${d.originZip} → ${d.destZip}`,
        crew: quote.crew.used,
        hours: `${quote.serviceLow}–${quote.serviceHigh}`,
        zone: quote.zone,
        reviewReasons: quote.reviewReasons,
        paymentMethod: d.paymentMethod,
        thirdPartyPayer: d.thirdPartyPayer,
        property: propertyFor(d.bedrooms, d.homeType),
        items: Object.entries(d.inventory).filter(([, q]) => q > 0).map(([id, qty]) => ({
          id,
          qty
        })),
        customItems: d.customItems.map(c => ({
          name: c.name,
          qty: c.qty
        })),
        specialItems: d.specialItems,
        stops: moveSummary.stops,
        services: moveSummary.services,
        truck: moveSummary.truck
      },
      consent: {
        quote: d.consentQuote,
        sms: d.consentSms,
        aiCall: d.consentAiCall,
        textVersion: CONSENT_VERSION,
        capturedAt: now,
        source: `funnel:${variant.key}`
      },
      outbox: res.mode === 'live' ? [{
        target: 'HighLevel — INSTAMOVERS FLORIDA',
        status: 'queued'
      }, {
        target: 'SmartMoving (transition)',
        status: 'queued'
      }] : [{
        target: 'HighLevel — INSTAMOVERS FLORIDA',
        status: 'not_sent_preview'
      }, {
        target: 'SmartMoving (transition)',
        status: 'not_sent_preview'
      }]
    };
    saveLead(lead);
    if (res.mode === 'live') {
      recordEvent('Lead', lead.id, {
        creative_key: variant.key
      });
      // Only after the backend persisted the request. Same event_id the server sends to CAPI, so Meta deduplicates.
      fireLead(`lead_${lead.id}`, {
        content_ids: [moveContentId(d.bedrooms)],
        content_category: d.homeType ?? 'unspecified',
        crew: quote.crew.used,
        packing: d.packing ?? 'unspecified',
        service_area: quote.zoneReasonKey
      });
    }
    setState(s => ({
      ...s,
      submitted: lead,
      step: STEP_STATUS
    }));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [state.data, state.attribution, quote, variant.key]);
  const reset = useCallback(() => {
    setState(s => ({
      data: initialData(variant, s.lang),
      step: 0,
      started: false,
      lang: s.lang,
      langManual: s.langManual,
      creativeKey: variant.key,
      submitted: null,
      sessionId: newId('S'),
      attribution: s.attribution
    }));
  }, [variant]);
  const value: FunnelCtx = {
    data: state.data,
    update,
    updateAccess,
    step: state.step,
    goTo,
    started: state.started,
    start,
    lang: state.lang,
    setLang: l => setState(s => ({
      ...s,
      lang: l,
      langManual: true,
      data: {
        ...s.data,
        commLang: l
      }
    })),
    t: (key, vars) => translate(state.lang, key, vars),
    variant,
    quote,
    config,
    estimateAccepted: state.data.acceptedQuoteHash === quote.hash,
    acceptEstimate,
    submitted: state.submitted,
    submit,
    reset,
    track
  };
  return <Ctx.Provider value={value} data-id="element-1075" data-mp="1heyup4ohueqq">{children}</Ctx.Provider>;
}
export function useFunnel(): FunnelCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useFunnel must be used inside FunnelProvider');
  return c;
}